import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider } from "@mui/material"
import IconButton from "@mui/material/IconButton"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC, ReactElement } from "react"
import { useCallback } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import useHandleCloseWithDataSave from "@/screens/Companies/edit/components/locations/hooks/useHandleCloseWithDataSave"
import { LocationContactMethodForm } from "@/screens/Companies/edit/ContactMethodForm"
import {
  type EditContactDialogProps,
  type EditContactQueryProps,
  EditContactQuerySchema,
} from "@/screens/Companies/types/dialogProps"

export const EditLocationContactMethodsDialog: FC<EditContactDialogProps> = ({
  data,
  onClose,
  onSubmit,
  activeEditLocation,
}): ReactElement => {
  const methods = useForm<EditContactQueryProps>({
    values: { ...(activeEditLocation as EditContactQueryProps), ...data },
    resolver: zodResolver(EditContactQuerySchema),
    mode: "all",
  })
  const handleSave = useCallback(
    async (value: EditContactQueryProps) => {
      const isValid = await methods.trigger()
      if (isValid) {
        onSubmit(value)
        onClose({ data: { ...value }, reason: "success" })
      }
    },
    [onSubmit, onClose, methods]
  )

  const handleClose = useHandleCloseWithDataSave(methods.getValues, onClose)

  return (
    <Dialog open={true} onClose={handleClose} fullWidth>
      <DialogTitle className='pl-10'>
        <span className='font-normal text-primary'>Edit Location Contact Methods</span>
      </DialogTitle>
      <IconButton aria-label='close' onClick={handleClose} className='absolute right-4 top-4 text-primary'>
        <CloseOutlined />
      </IconButton>
      <Divider />
      <DialogContent classes={{ root: "p-10" }}>
        <FormProvider {...methods}>
          <LocationContactMethodForm />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button variant='primary' appearance='outlined' onClick={handleClose}>
          Cancel
        </Button>
        <Button variant='primary' onClick={methods.handleSubmit(handleSave)} disabled={!methods.formState.isValid}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
